import React, { useState, useEffect, useRef } from 'react';
import MapView from './MapView';
import RideHistory from './RideHistory';
import locationService from '../services/locationService';
import fareCalculator from '../services/fareCalculator';
import apiService from '../services/apiService';
import sessionStorageService from '../services/sessionStorage';
import './TaxiMeter.css';

const TaxiMeter = () => {
    const [currentRide, setCurrentRide] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [route, setRoute] = useState([]);
    const [totalDistance, setTotalDistance] = useState(0);
    const [currentFare, setCurrentFare] = useState(620);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isRiding, setIsRiding] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState(null);
    
    const intervalRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        // セッションから状態を復元
        const savedSession = sessionStorageService.getSession();
        if (savedSession) {
            console.log('セッションから状態を復元中...');
            restoreFromSession(savedSession);
        } else {
            getCurrentLocation();
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            locationService.stopWatching(handleLocationUpdate);
        };
    }, []);

    useEffect(() => {
        if (isRiding) {
            locationService.startWatching(handleLocationUpdate);
        } else {
            locationService.stopWatching(handleLocationUpdate);
        }
    }, [isRiding]);

    useEffect(() => {
        if (isRiding && route.length > 1) {
            const distance = locationService.calculateRouteDistance(route);
            setTotalDistance(distance);
            const fare = fareCalculator.calculateFare(distance);
            setCurrentFare(fare);
            // セッションの距離と料金を更新
            sessionStorageService.updateDistanceAndFare(distance, fare);
        }
    }, [route, isRiding]);

    const getCurrentLocation = async () => {
        try {
            const position = await locationService.getCurrentPosition();
            setCurrentLocation(position);
            setError(null);
        } catch (err) {
            setError('位置情報の取得に失敗しました。位置情報を有効にしてください。');
            console.error('Location error:', err);
        }
    };

    const restoreFromSession = async (savedSession) => {
        try {
            console.log('セッションデータを復元中:', savedSession);
            
            // 基本的な状態を復元
            setCurrentRide(savedSession.currentRide);
            setCurrentLocation(savedSession.currentLocation);
            setRoute(savedSession.route || []);
            setTotalDistance(savedSession.totalDistance || 0);
            setCurrentFare(savedSession.currentFare || 620);
            setIsRiding(savedSession.isRiding || false);
            
            // 乗車中の場合、サーバーからの最新データを取得
            if (savedSession.isRiding && savedSession.currentRide) {
                try {
                    const response = await apiService.getRide(savedSession.currentRide.id);
                    if (response.success) {
                        console.log('サーバーから最新の乗車データを取得:', response.ride);
                        setCurrentRide(response.ride);
                        setRoute(response.ride.route || savedSession.route || []);
                        setTotalDistance(response.ride.distance || savedSession.totalDistance || 0);
                        setCurrentFare(response.ride.fare || savedSession.currentFare || 620);
                        
                        // セッションを最新データで更新
                        sessionStorageService.updateSession({
                            currentRide: response.ride,
                            route: response.ride.route || savedSession.route || [],
                            totalDistance: response.ride.distance || savedSession.totalDistance || 0,
                            currentFare: response.ride.fare || savedSession.currentFare || 620
                        });
                    }
                } catch (apiError) {
                    console.warn('サーバーからのデータ取得に失敗、セッションデータを使用:', apiError);
                }
            }
            
            // タイマーを復元
            if (savedSession.startTime) {
                startTimeRef.current = savedSession.startTime;
                const elapsed = Math.floor((Date.now() - savedSession.startTime) / 1000);
                setElapsedTime(elapsed);
                
                // タイマーを再開
                if (savedSession.isRiding) {
                    intervalRef.current = setInterval(() => {
                        const newElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
                        setElapsedTime(newElapsed);
                        sessionStorageService.updateElapsedTime(newElapsed);
                    }, 1000);
                }
            }
            
            // 現在位置を更新
            if (!savedSession.currentLocation) {
                getCurrentLocation();
            }
            
            setError(null);
            console.log('セッションからの復元が完了しました');
        } catch (err) {
            console.error('セッション復元エラー:', err);
            sessionStorageService.clearSession();
            getCurrentLocation();
        }
    };

    const handleLocationUpdate = (position, error) => {
        console.log('Location update called:', { position, error, isRiding });
        
        if (error) {
            console.error('Location update error:', error);
            setError('位置情報の更新に失敗しました。');
            return;
        }

        if (position) {
            console.log('Position updated:', position);
            setCurrentLocation(position);
            
            if (isRiding) {
                setRoute(prevRoute => {
                    const newRoute = [...prevRoute, position];
                    // セッションのルートを更新
                    sessionStorageService.updateRoute(position);
                    return newRoute;
                });
                console.log('Route updated');
            }
        }
    };

    const startRide = async () => {
        try {
            if (!currentLocation) {
                await getCurrentLocation();
                return;
            }

            const response = await apiService.startRide(currentLocation);
            
            if (response.success) {
                setCurrentRide(response.ride);
                setIsRiding(true);
                setRoute([currentLocation]);
                setTotalDistance(0);
                setCurrentFare(620);
                setElapsedTime(0);
                startTimeRef.current = Date.now();
                
                // セッションを開始
                sessionStorageService.startRideSession({
                    ride: response.ride,
                    startLocation: currentLocation
                });
                
                intervalRef.current = setInterval(() => {
                    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
                    setElapsedTime(elapsed);
                    sessionStorageService.updateElapsedTime(elapsed);
                }, 1000);
                
                setError(null);
            } else {
                setError('乗車開始に失敗しました。');
            }
        } catch (err) {
            setError('乗車開始に失敗しました。');
            console.error('Start ride error:', err);
        }
    };

    const endRide = async () => {
        try {
            if (!currentRide || !currentLocation) {
                return;
            }

            const response = await apiService.endRide(
                currentRide.id,
                currentLocation,
                totalDistance
            );
            
            if (response.success) {
                // タイマーを停止
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                
                // 位置情報の監視を停止
                locationService.stopWatching(handleLocationUpdate);
                
                // セッションを終了（storageをクリア）
                sessionStorageService.endRideSession();
                
                // 状態を完全に初期化
                setIsRiding(false);
                setCurrentRide(null);
                setRoute([]);
                setTotalDistance(0);
                setCurrentFare(620);
                setElapsedTime(0);
                startTimeRef.current = null;
                
                setError(null);
                
                // 乗車終了の通知
                alert(`乗車が終了しました。\n料金: ${fareCalculator.formatFare(response.ride.fare)}\n距離: ${fareCalculator.formatDistance(response.ride.distance)}\n時間: ${fareCalculator.formatDuration(response.ride.duration)}`);
                
                // 現在位置を再取得
                getCurrentLocation();
                
                console.log('乗車終了処理が完了しました');
            } else {
                setError('乗車終了に失敗しました。');
            }
        } catch (err) {
            setError('乗車終了に失敗しました。');
            console.error('End ride error:', err);
        }
    };

    const fareDetails = fareCalculator.getFareDetails(totalDistance);

    return (
        <div className="taxi-meter">
            <header className="meter-header">
                <h1>MakaMeter</h1>
                <button 
                    className="history-btn"
                    onClick={() => setShowHistory(!showHistory)}
                >
                    履歴
                </button>
            </header>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {showHistory ? (
                <RideHistory onClose={() => setShowHistory(false)} />
            ) : (
                <>
                    <div className="fare-display">
                        <div className="fare-amount">
                            {fareCalculator.formatFare(currentFare)}
                        </div>
                        <div className="fare-details">
                            <div>距離: {fareCalculator.formatDistance(totalDistance)}</div>
                            <div>時間: {fareCalculator.formatDuration(elapsedTime)}</div>
                            <div>基本料金: {fareCalculator.formatFare(fareDetails.baseFare)}</div>
                            {fareDetails.additionalAmount > 0 && (
                                <div>追加料金: {fareCalculator.formatFare(fareDetails.additionalAmount)}</div>
                            )}
                        </div>
                    </div>

                    <div className="map-container">
                        <MapView 
                            currentLocation={currentLocation}
                            route={route}
                            isRiding={isRiding}
                        />
                    </div>

                    <div className="controls">
                        {!isRiding ? (
                            <button 
                                className="start-btn"
                                onClick={startRide}
                                disabled={!currentLocation}
                            >
                                乗車開始
                            </button>
                        ) : (
                            <button 
                                className="end-btn"
                                onClick={endRide}
                            >
                                乗車終了
                            </button>
                        )}
                    </div>

                    <div className="status-info">
                        <div>状態: {isRiding ? '乗車中' : '待機中'}</div>
                        {currentLocation && (
                            <div className="location-info">
                                現在位置: {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
                                <br />
                                精度: {Math.round(currentLocation.accuracy)}m
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TaxiMeter;